// SPDX-License-Identifier: BUSL-1.1

/**
 * Moderation queue governance card — shows pending moderation items,
 * average wait time, and recent resolutions.
 *
 */

import type * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { apiFetch } from '@/lib/api-client';
import { relativeTime } from '@/lib/format';

interface ModerationItem {
  id: string;
  status: 'pending' | 'approved' | 'rejected';
  description: string;
  createdAt: string;
  resolvedAt?: string;
}

interface ModerationPendingResponse {
  items: ModerationItem[];
  total: number;
}

/** Compute average wait time in minutes for pending items. */
function avgWaitMinutes(items: ModerationItem[]): number {
  const pending = items.filter((i) => i.status === 'pending');
  if (pending.length === 0) return 0;
  const now = Date.now();
  const totalMs = pending.reduce(
    (sum, i) => sum + (now - new Date(i.createdAt).getTime()),
    0,
  );
  return totalMs / pending.length / 60_000;
}

export function GovernanceModerationCard(): React.ReactElement {
  const { data, isLoading } = useQuery({
    queryKey: ['moderation', 'pending'],
    queryFn: () => apiFetch<ModerationPendingResponse>('/moderation/pending'),
    refetchInterval: 15_000,
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm">Moderation Queue</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-7 w-16" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </CardContent>
      </Card>
    );
  }

  const items = data?.items ?? [];
  const pendingItems = items.filter((i) => i.status === 'pending');
  const resolvedItems = items
    .filter((i) => i.status !== 'pending')
    .slice(0, 3);
  const avgWait = avgWaitMinutes(pendingItems);

  const RESOLUTION_COLORS: Record<string, string> = {
    approved: 'text-green-400 bg-green-900/30',
    rejected: 'text-red-400 bg-red-900/30',
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-sm">Moderation Queue</CardTitle>
        <Link
          to="/inbox"
          className="text-xs text-neutral-500 transition hover:text-neutral-300"
        >
          Open inbox →
        </Link>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="flex items-baseline gap-3">
          <p
            className={`font-mono text-2xl font-semibold ${
              pendingItems.length > 0 ? 'text-amber-400' : 'text-neutral-100'
            }`}
          >
            {pendingItems.length}
          </p>
          <p className="text-xs text-neutral-500">pending</p>
        </div>

        <div>
          <p className="font-mono text-sm text-neutral-300">
            {avgWait > 0 ? `${Math.round(avgWait)}m` : '--'}
          </p>
          <p className="text-[11px] text-neutral-500">avg wait time</p>
        </div>

        {resolvedItems.length > 0 && (
          <div className="space-y-2 border-t border-neutral-800 pt-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
              Recent Resolutions
            </p>
            {resolvedItems.map((item) => (
              <div key={item.id} className="flex items-center gap-2">
                <Badge
                  variant="secondary"
                  className={`text-[11px] ${RESOLUTION_COLORS[item.status] ?? ''}`}
                >
                  {item.status}
                </Badge>
                <span className="min-w-0 flex-1 truncate text-xs text-neutral-400">
                  {item.description}
                </span>
                <time
                  className="shrink-0 font-mono text-[11px] text-neutral-500"
                  dateTime={item.resolvedAt ?? item.createdAt}
                >
                  {relativeTime(item.resolvedAt ?? item.createdAt)}
                </time>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
